import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

export type GradientTabVariant = 'blue' | 'green' | 'red' | 'purple' | 'amber' | 'default' | 'destructive';

// Remove all animation and drag-related event props to avoid conflicts between React and Framer Motion
type MotionButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>, 
  | 'onDrag' | 'onDragEnd' | 'onDragStart' | 'onDragEnter' | 'onDragLeave' | 'onDragOver' | 'onDragExit'
  | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
>;

export interface GradientButtonTabProps extends MotionButtonProps {
  variant?: GradientTabVariant;
  icon?: React.ReactNode;
  isActive?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  testId?: string;
}

/**
 * GradientButtonTab component with tab-like styling
 * Used for filter buttons and tabs with gradient backgrounds when active
 * and white background with black text when inactive
 */
const GradientButtonTab = React.forwardRef<HTMLButtonElement, GradientButtonTabProps>(
  ({ 
    children, 
    variant = 'blue',
    isActive = false,
    icon, 
    isLoading = false,
    fullWidth = false,
    className,
    disabled,
    testId,
    ...props 
  }, ref) => {
    // Gradient variants - all going from light (left) to dark (right)
    const gradients = {
      blue: 'bg-gradient-to-r from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700',
      green: 'bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700',
      red: 'bg-gradient-to-r from-red-400 to-red-600 hover:from-red-500 hover:to-red-700',
      purple: 'bg-gradient-to-r from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700',
      amber: 'bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700',
      default: 'bg-gradient-to-r from-gray-400 to-gray-600 hover:from-gray-500 hover:to-gray-700',
      destructive: 'bg-gradient-to-r from-red-400 to-red-600 hover:from-red-500 hover:to-red-700',
    };

    // Animation props for the motion button
    const motionProps = {
      whileHover: (!disabled && !isLoading) ? { 
        scale: 1.05, 
        y: -4,
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)'
      } : undefined,
      whileTap: (!disabled && !isLoading) ? { 
        scale: 0.97, 
        y: 0,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
      } : undefined,
      transition: { type: 'spring', stiffness: 400, damping: 15 }
    };

    return (
      <motion.button
        ref={ref}
        {...motionProps}
        disabled={disabled || isLoading}
        data-test-id={testId}
        data-active={isActive}
        className={cn(
          // Base styles
          'h-10 py-2 text-sm font-medium rounded-lg',
          'transition-all duration-200',
          'flex items-center justify-center lg:justify-start gap-2',
          // Responsive padding - equal on both sides when icon-only (on small screens)
          'px-5 xl:pl-5 xl:pr-6',
          'shadow-sm',  // Removed hover:shadow-md as we're using Framer Motion for this
          // Width
          fullWidth ? 'w-full' : 'w-auto',
          // Active/inactive styling
          isActive 
            ? cn('text-white', gradients[variant]) 
            : variant === 'blue' 
              ? 'bg-blue-50/80 text-blue-600 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800/30'
              : variant === 'purple' 
                ? 'bg-purple-50/80 text-purple-600 border border-purple-100 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800/30'
                : variant === 'red' 
                  ? 'bg-red-50/80 text-red-600 border border-red-100 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800/30'
                  : variant === 'green' 
                    ? 'bg-green-50/80 text-green-600 border border-green-100 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800/30'
                    : variant === 'amber' 
                      ? 'bg-amber-50/80 text-amber-600 border border-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800/30'
                      : 'bg-gray-50/80 text-gray-600 border border-gray-100 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800/30',
          // Disabled state
          (disabled || isLoading) && 'opacity-70 cursor-not-allowed hover:scale-100',
          // Custom classes
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{typeof children === 'string' ? 'Loading...' : children}</span>
          </>
        ) : (
          <>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            <span className="hidden lg:inline">{children}</span>
          </>
        )}
      </motion.button>
    );
  }
);

GradientButtonTab.displayName = 'GradientButtonTab';

export default GradientButtonTab;
